import React from 'react';
import classnames from 'classnames';
import { PapiUser } from '@wix/da-papi-types';
import Badge from '@wix/da-shared-react/pkg/Badge';
import UserLink from '@wix/da-shared-react/pkg/User/Link';
import Timestamp from '@wix/da-shared-react/pkg/Timestamp';
import shortenNumber from '@wix/da-shared-react/pkg/utils/shortenNumber';
import { BadgeActivityItem } from '../../../../../types/badge';
import { Trans, withTranslation, WithTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';

import s from './BadgeActivity.scss';

export interface Props {
  className?: string;
  isOwner?: boolean;
  ownerUsername: string;
  items: BadgeActivityItem[];
  totalSent: number;
  totalReceived: number;
}

interface BadgeRenderOptions {
  giver: PapiUser;
  receiver: PapiUser;
  badgeName: string;
  badgeId?: number;
}

export class BadgeActivity extends React.Component<Props & WithTranslation> {
  render() {
    return (
      <div className={classnames(s['root'], this.props.className)}>
        {this.renderTotalsRow()}
        {this.renderBadgesRows()}
      </div>
    );
  }

  renderTotalsRow() {
    return this.renderNotificationRow({
      label: this.renderTotalsLabel(),
      key: 'totals',
    });
  }

  renderBadgesRows() {
    const { items } = this.props;
    if (!items) {
      return null;
    }

    return items.map(item =>
      this.renderNotificationRow({
        label: this.renderBadgeLabel({
          giver: item.giver,
          receiver: item.receiver,
          badgeName: item.badge.title,
          badgeId: item.badge.id,
        }),
        icon: <Badge {...item.badge} className={s['icon']} />,
        timestamp: item.timestamp,
        key: `badge-${item.timestamp}`,
        receiverUsername: item.receiver.username,
        badgeId: item.badge.id,
      })
    );
  }

  renderUserLabel = (user: PapiUser, ownerClassname = '') => {
    const { isOwner, ownerUsername, t } = this.props;
    const isUserAnOwner = user.username === ownerUsername;

    return isOwner && isUserAnOwner ? (
      <span className={ownerClassname}>{t('common.you')}</span>
    ) : (
      // add span here so it matches the element count and i18n indeces of the 'you' above
      <span>
        <UserLink
          className={s['userlink']}
          showSymbol={false}
          user={user}
          withTooltip={true}
        />
      </span>
    );
  };

  renderBadgeLabel = ({
    giver,
    receiver,
    badgeName,
    badgeId,
  }: BadgeRenderOptions) => {
    return (
      <Trans i18nKey="widgets.notifications.badges.badge">
        {this.renderUserLabel(giver, s['giver-is-owner-label'])}
        {' gave '}
        {this.renderUserLabel(receiver)}
        {' a '}
        <a
          className={s['badge-name']}
          href={Url.badgesLink(receiver.username, badgeId)}
        >
          {badgeName}
        </a>
        Badge
      </Trans>
    );
  };

  renderTotalsLabel() {
    const { totalSent, totalReceived } = this.props;
    return (
      <div>
        <strong title={String(totalSent)}>{shortenNumber(totalSent)}</strong>{' '}
        <Trans
          i18nKey="widgets.notifications.badges.sent"
          count={totalSent}
          parent="span"
        >
          Badges sent
        </Trans>
        {', '}
        <strong title={String(totalReceived)}>
          {shortenNumber(totalReceived)}
        </strong>{' '}
        <Trans
          i18nKey="widgets.notifications.badges.received"
          count={totalReceived}
          parent="span"
        >
          Badges received
        </Trans>
        :
      </div>
    );
  }

  renderNotificationRow(props) {
    const { label, timestamp, icon, key, receiverUsername, badgeId } = props;
    return (
      <div className={s['row']} key={key}>
        <div className={s['notification']}>
          <div className={s['notification-label']}>{label}</div>
          {timestamp && (
            <div className={s['notification-ts']}>
              <Timestamp timestamp={timestamp} />
            </div>
          )}
        </div>
        {icon && (
          <a
            href={Url.badgesLink(receiverUsername, badgeId)}
            className={s['icon-wrapper']}
          >
            {icon}
          </a>
        )}
      </div>
    );
  }
}

export default withTranslation()(BadgeActivity);
